import { useState } from 'react';
import { Map as MapIcon } from 'lucide-react';
import { GlassPanel } from '@/components/glass/GlassPanel';
import {
  AMENITY_CATEGORIES,
  PROPERTY_COLOR_DEFAULT,
  PROPERTY_COLOR_IN_RADIUS,
  type AmenityCategory,
} from '@/lib/map-constants';
import { cn } from '@/lib/utils';

export interface MapLegendProps {
  visibleCategories: ReadonlySet<AmenityCategory>;
  categoryCounts: Record<AmenityCategory, number>;
  radiusMiles: number;
}

/**
 * `<MapLegend>` — glass-base panel, fixed bottom-right of the map area
 * at `md+`. Displays one row per amenity category (with live count) plus
 * a property-pin row and a radius-ring sample.
 *
 * UI-SPEC § 4.4:
 * - Active rows show the live count from `categoryCounts`
 * - Inactive rows show '—' + line-through styling
 * - Mobile (<md) collapses the panel into a 40×40 floating button that
 *   opens the same panel via local state (no Radix Popover dep needed —
 *   the button + absolute-positioned panel handle it inline)
 *
 * The panel is purely informational; the chips themselves are not
 * clickable here (the filter writes are delegated to `<MapFilterBar>`).
 */
export function MapLegend({
  visibleCategories,
  categoryCounts,
  radiusMiles,
}: MapLegendProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const PanelInner = (
    <GlassPanel
      variant="base"
      role="region"
      aria-label="Map legend"
      className="w-56 p-3 flex flex-col gap-2"
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-foam-50/60">
        Legend
      </h3>
      {AMENITY_CATEGORIES.map((meta) => {
        const active = visibleCategories.has(meta.db);
        const count = categoryCounts[meta.db] ?? 0;
        return (
          <div
            key={meta.db}
            className={cn(
              'flex items-center gap-2 text-sm text-foam-50',
              !active && 'opacity-40 line-through',
            )}
            aria-label={`${meta.label}: ${active ? count : 'hidden'} amenities`}
          >
            <span
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: meta.color }}
              aria-hidden
            />
            <span className="grow">{meta.label}</span>
            <span className="text-foam-50/60 tabular-nums">
              {active ? count : '—'}
            </span>
          </div>
        );
      })}
      {/* Property pin row — show out-of-radius + in-radius variants side by side */}
      <div className="flex items-center gap-2 text-sm text-foam-50 mt-2 pt-2 border-t border-foam-50/10">
        <span className="flex items-center gap-1 shrink-0">
          <span
            className="w-2 h-2 rounded-full border border-foam-50"
            style={{ backgroundColor: PROPERTY_COLOR_DEFAULT }}
            aria-hidden
          />
          <span
            className="w-3 h-3 rounded-full border border-foam-50"
            style={{ backgroundColor: PROPERTY_COLOR_IN_RADIUS }}
            aria-hidden
          />
        </span>
        <span>Property (out / in radius)</span>
      </div>
      {/* Radius ring row */}
      <div className="flex items-center gap-2 text-sm text-foam-50">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          aria-hidden
          className="shrink-0"
        >
          <circle
            cx="10"
            cy="10"
            r="8"
            stroke="#7faec9"
            strokeWidth="1.5"
            strokeOpacity="0.6"
            fill="#7faec9"
            fillOpacity="0.12"
          />
        </svg>
        <span>{radiusMiles.toFixed(1)} mi radius</span>
      </div>
    </GlassPanel>
  );

  return (
    <>
      {/* Desktop md+ : always-on panel */}
      <div className="hidden md:block absolute bottom-4 right-4 z-[400]">
        {PanelInner}
      </div>
      {/* Mobile <md: floating button + popover */}
      <div className="md:hidden absolute bottom-4 right-4 z-[400]">
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Open map legend"
          aria-expanded={mobileOpen}
          className={cn(
            'glass-base w-10 h-10 rounded-full flex items-center justify-center',
            'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
          )}
        >
          <MapIcon className="w-5 h-5 text-foam-50" aria-hidden />
        </button>
        {mobileOpen && (
          <div className="absolute bottom-12 right-0">{PanelInner}</div>
        )}
      </div>
    </>
  );
}
